import { BaseService } from '@/modules/Core/services/BaseService'
import type { IRequestConfig } from '@/modules/Core/interfaces/api/IApis'
import type {
  ICitaCuentaCobro,
  ICitaFacturaElectronica,
  ICitaCuentaPaquete,
  ICrearCuentaCobroAuditoriaPayload,
  ICrearCuentaCobroResultado
} from '@/modules/Financiera/interfaces'
import type { IResponseApi } from '@/modules/Core/interfaces/api/IApis'
import type { AxiosResponse } from 'axios'
import type { IConfCuentaCobroGeneral } from '@/modules/Financiera/interfaces/ICuentaCobro'
import type { IDataico } from '@/modules/Reportes/interfaces/dataico/IDataico'
import type { IDeshacerCuentaCobroResumen, IDeshacerCuentaSolicitud, IDeshacerCuentaResultado } from '@/modules/Financiera/interfaces/IDeshacerCuentaCobro'
export class CuentaCobroService extends BaseService<ICitaCuentaCobro> {
  constructor() {
    super({
      baseUrl: 'financiera/cuentas-cobro/'
    })
  }
  async generales(): Promise<AxiosResponse<IResponseApi<IConfCuentaCobroGeneral>>> {
    return this.http.get<IResponseApi<IConfCuentaCobroGeneral>>(`/general`)
  }
  async crearDesdeAuditoria(
    body: ICrearCuentaCobroAuditoriaPayload
  ): Promise<AxiosResponse<IResponseApi<ICrearCuentaCobroResultado>>> {
    return this.http
      .post<IResponseApi<ICrearCuentaCobroResultado>>('/create', body)
      .catch(this.errorHandle)
  }
  async validateInvoiceItems(
    id: number | string,
    params: Record<string,unknown>
  ): Promise<AxiosResponse<IResponseApi<ICitaCuentaPaquete[]>>> {
    return this.http.put<IResponseApi<ICitaCuentaPaquete[]>>(`/validate-invoices-items/${id}`,params)
  }
  async generateNumInvoice(
    id: number | string,
    body: Record<string,unknown>
  ): Promise<AxiosResponse<IResponseApi<ICitaCuentaPaquete[]>>> {
    return this.http
      .post<
        IResponseApi<ICitaCuentaPaquete[]>
      >(`/trigger-num-invoices/${id}`, body)
      .catch(this.errorHandle)
  }
  async createInvoiceOnly(
    id: string,
    body: Record<string, unknown>,
    config?: IRequestConfig
  ): Promise<AxiosResponse<IResponseApi<ICitaFacturaElectronica>>> {
    return this.http
      .post<IResponseApi<ICitaFacturaElectronica>>(`/create-invoice-only/${id}`, body, config)
      .catch(this.errorHandle)
  }
  async getServiciosByCuentaCobro(
    id: string
  ): Promise<AxiosResponse<IResponseApi<ICitaCuentaPaquete[]>>> {
    return this.http
      .post<
        IResponseApi<ICitaCuentaPaquete[]>
      >(`/get-services/${id}`)
      .catch(this.errorHandle)
  }
  async traslateInvoiceItems(
    id: string,
    idNew: number,
    params: Record<string,unknown>
  ): Promise<AxiosResponse<IResponseApi<ICitaCuentaPaquete[]>>> {
    return this.http.put<IResponseApi<ICitaCuentaPaquete[]>>(`/traslate/${id}/${idNew}`,params)
  }
  async sendToDataico(
    id: string, 
    body: Record<string,unknown>
  ): Promise<AxiosResponse<IResponseApi<ICitaCuentaPaquete[]>>> {
    return this.http.post<IResponseApi<ICitaCuentaPaquete[]>>(`/send-to-dataico/${id}`, body)
  }
  async previewInvoice(ciccId: number | string, body: Record<string, unknown>): Promise<AxiosResponse<IResponseApi<IDataico>>> {
    return this.http.post<IResponseApi<IDataico>>(`/preview-invoice/${ciccId}`, body)
  }
  async anular(
    id: number | string,
    body: IDeshacerCuentaSolicitud,
    config?: IRequestConfig
  ): Promise<AxiosResponse<IResponseApi<IDeshacerCuentaResultado>>> {
    const respuesta = await this.http.delete<IResponseApi<IDeshacerCuentaResultado>>(`/anular/${id}`, { data: body, ...config })
    return respuesta
  }
  async eliminarCuenta(
    id: number | string,
    motivo: string,
    config?: IRequestConfig
  ): Promise<AxiosResponse<IResponseApi<{ mensaje: string }>>> {
    const respuesta = await this.http.delete<IResponseApi<{ mensaje: string }>>(
      `/eliminar/${id}`,
      { data: { motivo }, ...config }
    )
    return respuesta
  }
  async resumenDeshacer(id: number | string): Promise<AxiosResponse<IResponseApi<IDeshacerCuentaCobroResumen>>> {
    return this.http.get<IResponseApi<IDeshacerCuentaCobroResumen>>(`/resumen-deshacer/${id}`)
  }
}
